const customDataStore = new Map<string, string>();

const MAX_FIELDS = 50;
const MAX_KEY_LENGTH = 100;
const MAX_VALUE_LENGTH = 5000;
const SESSION_STORAGE_KEY = 'brij_custom_data';

const isObjectOrArray = (value: any): boolean => {
  return (
    value !== null &&
    typeof value === 'object' &&
    (Array.isArray(value) ||
      Object.prototype.toString.call(value) === '[object Object]')
  );
};

const loadFromSessionStorage = (): void => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        Object.entries(parsed).forEach(([key, value]) => {
          if (typeof key === 'string' && typeof value === 'string') {
            customDataStore.set(key, value);
          }
        });
      }
    }
  } catch (error) {
    console.warn(
      '[brij.customData] Failed to load from sessionStorage:',
      error
    );
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
};

const saveToSessionStorage = (): void => {
  try {
    const data = getCustomData();
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('[brij.customData] Failed to save to sessionStorage:', error);
  }
};

export const customData = (
  key: string,
  value: string
): { success: boolean; error?: string } => {
  if (key === undefined || key === null || key === '') {
    const error = '[brij.customData] Error: Key cannot be empty';
    console.error(error);
    return { success: false, error };
  }
  if (isObjectOrArray(key)) {
    const error = '[brij.customData] Error: Key cannot be an object or array';
    console.error(error);
    return { success: false, error };
  }

  const keyStr = String(key);
  if (keyStr.length > MAX_KEY_LENGTH) {
    const error = `[brij.customData] Error: Key exceeds maximum length of ${MAX_KEY_LENGTH} characters (got ${keyStr.length})`;
    console.error(error);
    return { success: false, error };
  }

  if (value === undefined || value === null) {
    const error = '[brij.customData] Error: Value cannot be null or undefined';
    console.error(error);
    return { success: false, error };
  }

  if (isObjectOrArray(value)) {
    const error =
      '[brij.customData] Error: Value cannot be an object or array. Please convert to JSON string first.';
    console.error(error);
    return { success: false, error };
  }

  const valueStr = String(value);
  if (valueStr.length > MAX_VALUE_LENGTH) {
    const error = `[brij.customData] Error: Value exceeds maximum length of ${MAX_VALUE_LENGTH} characters (got ${valueStr.length})`;
    console.error(error);
    return { success: false, error };
  }

  if (!customDataStore.has(keyStr) && customDataStore.size >= MAX_FIELDS) {
    const error = `[brij.customData] Error: Maximum of ${MAX_FIELDS} fields reached. Cannot add new key "${keyStr}"`;
    console.error(error);
    return { success: false, error };
  }

  customDataStore.set(keyStr, valueStr);
  saveToSessionStorage();

  return { success: true };
};

export const getCustomData = (): Record<string, string> => {
  const data: Record<string, string> = {};
  customDataStore.forEach((value, key) => {
    data[key] = value;
  });
  return data;
};

export const clearCustomData = (): void => {
  customDataStore.clear();
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

export const getCustomDataInfo = (): {
  fieldCount: number;
  maxFields: number;
  remainingFields: number;
} => {
  return {
    fieldCount: customDataStore.size,
    maxFields: MAX_FIELDS,
    remainingFields: MAX_FIELDS - customDataStore.size,
  };
};

export const initializeCustomData = (): void => {
  loadFromSessionStorage();
  (window as any).brij = {
    customData,
    getCustomData,
    clearCustomData,
    getCustomDataInfo,
  };
};
